import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FileText, Save } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BookingFormLabelsEditor = () => {
  const [labels, setLabels] = useState({
    modalTitle: 'Book Appointment',
    selectSubjectLabel: 'Select Pet',
    selectSubjectPlaceholder: 'Choose a pet...',
    noSubjectsMessage: "This client doesn't have any pets yet. Please add a pet first.",
    selectServiceLabel: 'Select Service',
    selectServicePlaceholder: 'Choose a service...',
    selectGroomerLabel: 'Select Groomer',
    selectGroomerOptional: '(Optional)',
    selectGroomerPlaceholder: 'Any available groomer',
    dateLabel: 'Date',
    timeLabel: 'Time',
    timePlaceholder: 'Choose a time slot...',
    loadingTimesMessage: 'Loading available times...',
    noTimesMessage: 'No available time slots for this date',
    addExtrasLabel: 'Add Popular Extras',
    specialRequestsLabel: 'Special Requests',
    specialRequestsPlaceholder: 'Any special instructions or requests...',
    submitButton: 'Book Appointment',
    submittingButton: 'Booking...',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadLabels();
  }, []);

  const loadLabels = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/business-info/booking_form_labels`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.info_value) {
        const savedLabels = JSON.parse(response.data.info_value);
        setLabels(prev => ({
          ...prev,
          ...savedLabels,
        }));
      }
    } catch (error) {
      console.error('Load labels error:', error);
      // Keep defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setLabels(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/business-info/booking_form_labels`,
        { value: JSON.stringify(labels) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking form labels updated successfully!');
    } catch (error) {
      console.error('Save labels error:', error);
      toast.error('Failed to save booking form labels');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <FileText className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Booking Form Labels</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Customize the text labels used in the employee/admin booking form. These labels will automatically sync with your Service Subjects settings.
      </p>

      <div className="space-y-6">
        {/* Modal Title */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Modal Header</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Modal Title
              </label>
              <input
                type="text"
                value={labels.modalTitle}
                onChange={(e) => handleChange('modalTitle', e.target.value)}
                className="input w-full"
                placeholder="e.g., Book Appointment"
              />
            </div>
          </div>
        </div>

        {/* Subject/Pet Selection */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Subject Selection (Pet/Lawn/Vehicle)</h3>
          <p className="text-xs text-gray-600 mb-3">
            Note: The word "Pet" will automatically change based on your Service Subjects settings
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Label
              </label>
              <input
                type="text"
                value={labels.selectSubjectLabel}
                onChange={(e) => handleChange('selectSubjectLabel', e.target.value)}
                className="input w-full"
                placeholder="e.g., Select Pet"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dropdown Placeholder
              </label>
              <input
                type="text"
                value={labels.selectSubjectPlaceholder}
                onChange={(e) => handleChange('selectSubjectPlaceholder', e.target.value)}
                className="input w-full"
                placeholder="e.g., Choose a pet..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                No Subjects Message
              </label>
              <input
                type="text"
                value={labels.noSubjectsMessage}
                onChange={(e) => handleChange('noSubjectsMessage', e.target.value)}
                className="input w-full"
                placeholder="e.g., This client doesn't have any pets yet..."
              />
            </div>
          </div>
        </div>

        {/* Service Selection */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Service Selection</h3>
          <p className="text-xs text-gray-600 mb-3">
            Services are automatically loaded from your Services settings
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Label
              </label>
              <input
                type="text"
                value={labels.selectServiceLabel}
                onChange={(e) => handleChange('selectServiceLabel', e.target.value)}
                className="input w-full"
                placeholder="e.g., Select Service"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dropdown Placeholder
              </label>
              <input
                type="text"
                value={labels.selectServicePlaceholder}
                onChange={(e) => handleChange('selectServicePlaceholder', e.target.value)}
                className="input w-full"
                placeholder="e.g., Choose a service..."
              />
            </div>
          </div>
        </div>

        {/* Groomer Selection */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Groomer/Staff Selection</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Label
              </label>
              <input
                type="text"
                value={labels.selectGroomerLabel}
                onChange={(e) => handleChange('selectGroomerLabel', e.target.value)}
                className="input w-full"
                placeholder="e.g., Select Groomer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Optional Text
              </label>
              <input
                type="text"
                value={labels.selectGroomerOptional}
                onChange={(e) => handleChange('selectGroomerOptional', e.target.value)}
                className="input w-full"
                placeholder="e.g., (Optional)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dropdown Placeholder
              </label>
              <input
                type="text"
                value={labels.selectGroomerPlaceholder}
                onChange={(e) => handleChange('selectGroomerPlaceholder', e.target.value)}
                className="input w-full"
                placeholder="e.g., Any available groomer"
              />
            </div>
          </div>
        </div>

        {/* Date & Time */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Date & Time Selection</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Label
              </label>
              <input
                type="text"
                value={labels.dateLabel}
                onChange={(e) => handleChange('dateLabel', e.target.value)}
                className="input w-full"
                placeholder="e.g., Date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Label
              </label>
              <input
                type="text"
                value={labels.timeLabel}
                onChange={(e) => handleChange('timeLabel', e.target.value)}
                className="input w-full"
                placeholder="e.g., Time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Placeholder
              </label>
              <input
                type="text"
                value={labels.timePlaceholder}
                onChange={(e) => handleChange('timePlaceholder', e.target.value)}
                className="input w-full"
                placeholder="e.g., Choose a time slot..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loading Times Message
              </label>
              <input
                type="text"
                value={labels.loadingTimesMessage}
                onChange={(e) => handleChange('loadingTimesMessage', e.target.value)}
                className="input w-full"
                placeholder="e.g., Loading available times..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                No Times Available Message
              </label>
              <input
                type="text"
                value={labels.noTimesMessage}
                onChange={(e) => handleChange('noTimesMessage', e.target.value)}
                className="input w-full"
                placeholder="e.g., No available time slots for this date"
              />
            </div>
          </div>
        </div>

        {/* Add-ons */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Add-ons/Extras</h3>
          <p className="text-xs text-gray-600 mb-3">
            Add-ons are automatically loaded from your Add-ons settings
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section Label
              </label>
              <input
                type="text"
                value={labels.addExtrasLabel}
                onChange={(e) => handleChange('addExtrasLabel', e.target.value)}
                className="input w-full"
                placeholder="e.g., Add Popular Extras"
              />
            </div>
          </div>
        </div>

        {/* Special Requests */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Special Requests</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label
              </label>
              <input
                type="text"
                value={labels.specialRequestsLabel}
                onChange={(e) => handleChange('specialRequestsLabel', e.target.value)}
                className="input w-full"
                placeholder="e.g., Special Requests"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder
              </label>
              <input
                type="text"
                value={labels.specialRequestsPlaceholder}
                onChange={(e) => handleChange('specialRequestsPlaceholder', e.target.value)}
                className="input w-full"
                placeholder="e.g., Any special instructions or requests..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Submit Button</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button Text
              </label>
              <input
                type="text"
                value={labels.submitButton}
                onChange={(e) => handleChange('submitButton', e.target.value)}
                className="input w-full"
                placeholder="e.g., Book Appointment"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Submitting Text
              </label>
              <input
                type="text"
                value={labels.submittingButton}
                onChange={(e) => handleChange('submittingButton', e.target.value)}
                className="input w-full"
                placeholder="e.g., Booking..."
              />
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> These labels apply to the employee/admin booking form. Services and add-ons are automatically pulled from your Services and Add-ons settings. Subject terminology (Pet/Lawn/Vehicle) syncs with your Service Subjects configuration.
          </p>
        </div>

        {/* Save Button */}
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Ready to save your changes?</h4>
              <p className="text-sm text-gray-600">Click the button to save all booking form label customizations.</p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary flex items-center space-x-2 text-lg px-6 py-3"
            >
              <Save size={20} />
              <span>{saving ? 'Saving...' : 'Save Labels'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFormLabelsEditor;
